/**
 * Nucleo de las notas ancladas: todo lo que toca el DOM y nada del framework.
 *
 * Viene de la guia de Vantage (docs/notas-ancladas.md). Se mantiene sin
 * dependencias y sin nada de React a proposito: es la pieza que se copia tal
 * cual a la siguiente aplicacion que quiera el mismo sistema.
 */

/**
 * Elementos que NO cuentan al construir ni al resolver una ruta estructural.
 *
 * Es la trampa numero uno de la guia: un toast que aparece y desaparece cambia
 * los indices de `nth-of-type`, asi que una nota creada mientras estaba visible
 * acaba resolviendo a OTRO elemento cuando ya no lo esta. El constructor y el
 * resolvedor tienen que filtrar exactamente igual — por eso el resolvedor
 * recorre los hijos a mano en vez de usar `querySelector`, que no sabe saltarse
 * nada.
 */
export const isVolatile = (el: Element): boolean =>
  el.hasAttribute("data-note-widget") || el.hasAttribute("data-note-skip");

/**
 * Escapa un valor para meterlo entre comillas dobles en un selector de
 * atributo. NO vale `CSS.escape`, que escapa identificadores: aplicado a
 * `seller:clay:A1` produciria `seller\:clay\:A1`, que dentro de comillas es
 * otra cosa. Aqui solo hay que neutralizar la barra invertida y la comilla.
 */
const quoteAttr = (v: string): string =>
  v.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

/**
 * Ruta estructural compacta hasta un elemento: se ancla en cuanto encuentra un
 * `id` o un `[data-note]` antepasado, y desde ahi baja con `tag:nth-of-type`.
 */
export function cssPath(el: Element): string | null {
  const parts: string[] = [];
  let node: Element | null = el;

  while (node && parts.length < 14) {
    if (node.id) {
      parts.unshift(`#${CSS.escape(node.id)}`);
      break;
    }
    const anchor = node.getAttribute("data-note");
    if (anchor) {
      parts.unshift(`[data-note="${quoteAttr(anchor)}"]`);
      break;
    }
    const tag = node.tagName.toLowerCase();
    if (tag === "body" || tag === "html") {
      parts.unshift("body");
      break;
    }
    const parent: Element | null = node.parentElement;
    if (!parent) {
      parts.unshift(tag);
      break;
    }
    const actual = node;
    const sameTag = [...parent.children].filter(
      (c) => c.tagName === actual.tagName && !isVolatile(c),
    );
    parts.unshift(
      sameTag.length > 1 ? `${tag}:nth-of-type(${sameTag.indexOf(actual) + 1})` : tag,
    );
    node = parent;
  }

  const sel = parts.join(" > ");
  return sel.length > 0 && sel.length <= 500 ? sel : null;
}

/** Resuelve una ruta de `cssPath`, saltandose los volatiles igual que ella. */
export function resolvePath(path: string): Element | null {
  const parts = path.split(" > ");
  let scope: Element | null = null;

  for (const [i, part] of parts.entries()) {
    if (i === 0) {
      scope = part === "body" ? document.body : document.querySelector(part);
      if (!scope) return null;
      continue;
    }
    const m = /^([a-z0-9-]+)(?::nth-of-type\((\d+)\))?$/.exec(part);
    if (!m || !scope) return null;
    const [, tag, index] = m;
    const candidates: Element[] = [...scope.children].filter(
      (c) => c.tagName.toLowerCase() === tag && !isVolatile(c),
    );
    scope = candidates[(index ? Number(index) : 1) - 1] ?? null;
    if (!scope) return null;
  }
  return scope;
}

/**
 * Etiqueta legible de una zona de la pagina: el encabezado mas cercano.
 *
 * NUNCA el `innerText` del contenedor: en la bandeja de entrada eso produce un
 * bloque de texto ilegible en vez de un titulo.
 */
export function nearestHeading(el: Element): string | null {
  let node: Element | null = el;
  while (node && node !== document.body) {
    const h = node.querySelector("h1, h2, h3");
    const t = h?.textContent?.trim();
    if (t) return t.replace(/\s+/g, " ").slice(0, 255);
    node = node.parentElement;
  }
  return null;
}

export interface AnchorLike {
  entity_type?: string | null;
  entity_id?: string | null;
  selector?: string | null;
}

/** Una sola clave por nota, para agrupar los pines y para localizarla luego. */
export function noteKey(n: AnchorLike): string | null {
  if (n.entity_type && n.entity_id) return `ent|${n.entity_type}:${n.entity_id}`;
  if (n.selector) return `sel|${n.selector}`;
  return null; // de pagina: no hay elemento que localizar
}

export function locate(key: string): Element | null {
  const kind = key.slice(0, 3);
  const value = key.slice(4);
  try {
    return kind === "ent"
      ? document.querySelector(`[data-note="${quoteAttr(value)}"]`)
      : resolvePath(value);
  } catch {
    return null;
  }
}

/**
 * Resalta un elemento con `outline`.
 *
 * Ni una capa posicionada por `getBoundingClientRect()` —se descuadra al hacer
 * scroll y la recorta cualquier antepasado con `overflow:hidden`— ni
 * `box-shadow`, que ademas lo pisan las propias clases de anillo/sombra del
 * elemento. El `outline` se dibuja fuera de la caja, no se recorta y se mueve
 * con el elemento. La guia dice que esto costo horas de depuracion.
 */
export function highlight(el: HTMLElement): void {
  const prev = {
    outline: el.style.outline,
    offset: el.style.outlineOffset,
    transition: el.style.transition,
  };
  el.style.transition = "outline-color .3s ease";
  el.style.outline = "3px solid var(--primary, #ee981f)";
  el.style.outlineOffset = "3px";
  window.setTimeout(() => {
    el.style.outlineColor = "transparent";
  }, 2200);
  window.setTimeout(() => {
    el.style.outline = prev.outline;
    el.style.outlineOffset = prev.offset;
    el.style.transition = prev.transition;
  }, 2600);
}

export interface PinRect {
  top: number;
  left: number;
}

/**
 * Donde va el pin de un elemento, en coordenadas de viewport (position: fixed).
 * `null` si el elemento no ocupa nada: eso significa que esta en otra pestaña o
 * en otra pagina de una tabla, y un pin en la esquina superior izquierda seria
 * peor que ninguno.
 */
export function pinRectFor(el: Element): PinRect | null {
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top - 10, left: r.right - 10 };
}

/**
 * Lee el ancla de entidad del antepasado `[data-note]` mas cercano.
 * Es el ancla preferida: se resuelve con un solo `querySelector` estable, y
 * sobrevive a re-renders, reordenaciones y paginacion.
 */
export function entityAnchorOf(
  el: Element,
): { type: string; id: string; label: string | null } | null {
  const host = el.closest("[data-note]");
  if (!host) return null;
  const raw = host.getAttribute("data-note") ?? "";
  const i = raw.indexOf(":");
  if (i <= 0 || i === raw.length - 1) return null;
  const label =
    host.getAttribute("data-note-label")?.trim() ||
    host.textContent?.trim().replace(/\s+/g, " ").slice(0, 255) ||
    null;
  return { type: raw.slice(0, i), id: raw.slice(i + 1), label };
}

/** La ruta actual sin query ni fragmento, que es como se guarda en `notes.path`. */
export function currentPath(): string {
  const p = window.location.pathname;
  return p.length > 1 ? p.replace(/\/+$/, "") : p;
}
